import { NavLink, Outlet } from 'react-router-dom'
import { useEffect, useState } from 'react'
import OsteoLogo from './components/Logo'
import { getMe } from './services/api'
import { GH_SCRIPTS } from './ghScripts'

/* global __APP_VERSION__ */
const APP_VERSION = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'dev'

// The design toolset — STL Preview is the design-portal-native tool; the rest were
// moved here from the ops portal (kept as-is) so the design workflow is independent.
//
// An entry with `children` renders as a titled group instead of a link. The Grasshopper
// tools are grouped because there are now two kinds of them — the generator, which
// writes fresh code from a prompt, and the curated, tested library in ghScripts.js —
// and the group is built from that registry, so a new script needs no change here.
const TOOLS = [
  { to: '/', label: 'STL/OBJ to PDF', end: true },
  { to: '/packaging', label: 'Packaging Illustration' },
  { to: '/convert', label: 'Convert · STL→GLB' },
  { to: '/bg-remove', label: 'BG Remove' },
  { to: '/case-report', label: 'Case Report' },
  // The mesh-driven builder. Kept SEPARATE from 'Case Report' above, which
  // composites PSDs into a fixed template and is still in daily use.
  { to: '/case-report-builder', label: 'Case Report Builder' },
  { to: '/stl-clean', label: 'STL Clean' },
  { to: '/png-to-pdf', label: 'PNG → PDF' },
  {
    label: 'GH Scripts',
    children: [
      { to: '/gh-generator', label: 'Script Generator' },
      ...GH_SCRIPTS.map((s) => ({ to: s.route, label: s.label })),
    ],
  },
]

const linkClass = ({ isActive }) =>
  [
    'block px-3 py-2 rounded-lg text-sm font-medium transition-colors',
    isActive ? 'bg-brand-50 text-brand-700' : 'text-gray-600 hover:bg-gray-100',
  ].join(' ')

export default function ToolsLayout() {
  const [email, setEmail] = useState('')
  useEffect(() => { getMe().then((r) => setEmail(r.data.email)).catch(() => {}) }, [])
  function signOut() {
    sessionStorage.removeItem('adminToken')
    window.location.href = '/login'
  }

  return (
    <div className="min-h-screen flex flex-col">
      <header className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <OsteoLogo size="sm" />
          <div className="border-l border-gray-200 pl-4">
            <p className="text-sm font-semibold text-gray-700 leading-none">Design Portal</p>
            <p className="text-xs text-gray-400 mt-0.5">Design &amp; illustration tools</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          {email && <span className="text-sm text-gray-600 hidden sm:block">{email}</span>}
          <button onClick={signOut} className="btn-secondary text-sm px-4 py-2">Sign out</button>
        </div>
      </header>

      <div className="flex-1 flex">
        <aside className="w-52 shrink-0 border-r border-gray-200 bg-white p-3 space-y-1">
          {TOOLS.map((t) => (
            t.children ? (
              <div key={t.label} className="pt-3">
                <p className="px-3 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                  {t.label}
                </p>
                <div className="space-y-1 border-l border-gray-100 ml-3 pl-1">
                  {t.children.map((c) => (
                    <NavLink key={c.to} to={c.to} className={linkClass}>
                      {c.label}
                    </NavLink>
                  ))}
                </div>
              </div>
            ) : (
              <NavLink key={t.to} to={t.to} end={t.end} className={linkClass}>
                {t.label}
              </NavLink>
            )
          ))}
        </aside>

        <main className="flex-1 min-w-0 p-6 bg-gray-50">
          <Outlet />
        </main>
      </div>

      <footer className="text-center text-xs text-gray-400 py-4 bg-white border-t border-gray-100">
        Osteo3d · 3d printing healthcare · <a href="https://osteo3d.com" className="hover:text-gray-600">osteo3d.com</a> · {APP_VERSION}
      </footer>
    </div>
  )
}
